<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import 'chartjs-adapter-moment';

	import {
		Chart,
		LineController,
		LineElement,
		PointElement,
		LinearScale,
		Title,
		CategoryScale,
		Filler,
		Tooltip,
		TimeScale
	} from 'chart.js';
	import { getNumBackInUnits } from '$lib/util/util';
	import {
		chartSelected,
		currentMarketIdentifier,
		fundingRateInterval,
		loadingMarketTimeSeriesPoints,
		marketTimeSeriesPoints
	} from '../store';
	import { getCurrentMarketTimeSeriesPoints } from '$lib/data/fundingRate';
	import BigNumber from 'bignumber.js';
	import { page } from '$app/stores';

	export let showBorder = true;

	Chart.register(
		LineController,
		LineElement,
		PointElement,
		LinearScale,
		Title,
		CategoryScale,
		Filler,
		Tooltip,
		TimeScale
	);

	Chart.defaults.scale.grid.display = true;
	let isChartCreated = false;
	let gradientLineLength = 400;

	const getOrCreateTooltip = (chart) => {
		let tooltipEl = chart.canvas.parentNode.querySelector('div');

		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.style.opacity = 1;
			tooltipEl.style.pointerEvents = 'none';
			tooltipEl.style.position = 'absolute';
			tooltipEl.style.transform = 'translate(-50%, 0)';
			tooltipEl.style.transition = 'all .1s ease';

			const innerTooltip = document.createElement('div');

			tooltipEl.appendChild(innerTooltip);
			chart.canvas.parentNode.appendChild(tooltipEl);
		}

		return tooltipEl;
	};

	const externalTooltipHandler = (context) => {
		// Tooltip Element
		const { chart, tooltip } = context;
		const tooltipEl = getOrCreateTooltip(chart);
		// Hide if no tooltip
		if (tooltip.opacity === 0) {
			tooltipEl.style.opacity = 0;
			return;
		}
		if (tooltip.body) {
			const tooltipRoot = tooltipEl.querySelector('div');

			// Remove old children
			if (tooltipRoot) {
				while (tooltipRoot.firstChild) {
					tooltipRoot.firstChild.remove();
				}
				if (
					tooltip &&
					tooltip.title &&
					tooltip.body &&
					tooltip.body.length > 0 &&
					tooltip.body[0].lines
				) {
					// Add new children
					tooltipRoot.innerHTML =
						`<div class="p-px bg-fx-gradient-6 rounded-xl">
						<div class="bg-fx-black backdrop-filter backdrop-blur-sm opacity-70 p-2 rounded-xl">
							<div class="flex justify-between items-center gap-1">
								<div class="text-fx-grey-400 text-xs">Time:</div>
								<div class="text-fx-grey-200 text-sm whitespace-nowrap">` +
						tooltip.title +
						`</div>
							</div>
							<div class="flex justify-between items-center">
								<div class="text-fx-grey-400 text-xs">Funding Rate:</div>
								<div class="text-fx-yellow text-sm whitespace-nowrap">` +
						tooltip.body[0].lines[0] +
						`%</div>
							</div>
						</div>
					</div>
					`;
				}
			}
		}

		const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

		// Display, position, and set styles for font
		tooltipEl.style.opacity = 1;
		tooltipEl.style.left = positionX + tooltip.caretX - (tooltip.caretX > 200 ? 100 : -100) + 'px';
		tooltipEl.style.top = positionY + tooltip.caretY - (tooltip.caretY > 200 ? 100 : -100) + 'px';
		tooltipEl.style.font = tooltip.options.bodyFont.string;
	};

	async function createChart() {
		let yieldData: any = [];
		let zeroLine: any = [];
		let suggestedMin: number = 1000000000000000000000000;
		let suggestedMax: number = 0;
		$marketTimeSeriesPoints?.forEach(function (c) {
			let y = '0';
			if ($fundingRateInterval == '1H') {
				y = getNumBackInUnits(new BigNumber(c.fundingRate).div(24).toString(), 16, 6);
			} else if ($fundingRateInterval == '1Y') {
				y = getNumBackInUnits(new BigNumber(c.fundingRate).multipliedBy(365).toString(), 16, 6);
			} else {
				y = getNumBackInUnits(c.fundingRate, 16, 6);
			}
			var obj = {
				x: c.timestamp,
				y: y
			};
			yieldData.push(obj);
			zeroLine.push({
				x: c.timestamp,
				y: 0
			});
			if (parseFloat(obj.y) > suggestedMax) {
				suggestedMax = parseFloat(obj.y);
			}
			if (parseFloat(obj.y) < suggestedMin) {
				suggestedMin = parseFloat(obj.y);
			}
		});
		suggestedMin = suggestedMax * 0.95;
		suggestedMax = suggestedMax * 1.02;
		yieldData.reverse();
		let perChart = document.getElementById('fundingRateChartElement');
		if (!perChart) return;
		var ctx = perChart.getContext('2d');
		var gradient = ctx.createLinearGradient(0, 0, 0, gradientLineLength);
		var gradient = ctx.createLinearGradient(0, 0, 0, gradientLineLength);
		gradient.addColorStop(0, '#85A0FF');
		gradient.addColorStop(1, '#4030FF');
		var chart = new Chart(ctx, {
			type: 'line',
			data: {
				datasets: [
					{
						fill: 'start',
						pointRadius: 0,
						borderColor: gradient,
						borderWidth: 2,
						data: yieldData,
						pointHoverRadius: 5,
						pointHoverBackgroundColor: '#FFD99F'
					},
					{
						fill: 'start',
						pointRadius: 0,
						borderColor: '#4c4c4c',
						borderWidth: 1,
						data: zeroLine
					}
				]
			},
			options: {
				maintainAspectRatio: false,
				responsive: true,
				interaction: {
					intersect: false
				},
				scales: {
					x: {
						display: true,
						type: 'time',
						time: {
							tooltipFormat: 'hh:mm a DD MMM',
							minUnit: 'day'
						}
					},

					y: {
						display: true,
						suggestedMax: suggestedMax,
						suggestedMin: suggestedMin,
						grid: {
							display: true,
							color: '#161825'
						}
					}
				},
				plugins: {
					legend: {
						display: true
					},
					tooltip: {
						filter: function (tooltipItem) {
							return tooltipItem.datasetIndex === 0;
						},
						enabled: false,
						external: externalTooltipHandler
					}
				}
			}
		});
		isChartCreated = true;
		return chart;
	}

	async function updateChartData() {
		let yieldData: any = [];
		let zeroLine: any = [];
		$marketTimeSeriesPoints?.forEach(function (c) {
			let y = '0';
			if ($fundingRateInterval == '1H') {
				y = getNumBackInUnits(new BigNumber(c.fundingRate).div(24).toString(), 16, 6);
			} else if ($fundingRateInterval == '1Y') {
				y = getNumBackInUnits(new BigNumber(c.fundingRate).multipliedBy(365).toString(), 16, 6);
			} else {
				y = getNumBackInUnits(c.fundingRate, 16, 6);
			}
			var obj = {
				x: c.timestamp,
				y: y
			};
			yieldData.push(obj);
			zeroLine.push({
				x: c.timestamp,
				y: 0
			});
		});
		yieldData.reverse();
		let perChart = document.getElementById('fundingRateChartElement');
		if (!perChart) return;
		var ctx = perChart.getContext('2d');
		var gradient = ctx.createLinearGradient(0, 0, 0, gradientLineLength);
		var gradient = ctx.createLinearGradient(0, 0, 0, gradientLineLength);
		gradient.addColorStop(0, '#85A0FF');
		gradient.addColorStop(1, '#4030FF');
		userPortfolioChart.data = {
			datasets: [
				{
					fill: 'start',
					pointRadius: 0,
					borderColor: gradient,
					borderWidth: 2,
					data: yieldData,
					pointHoverRadius: 5,
					pointHoverBackgroundColor: '#FFD99F'
				},
				{
					fill: 'start',
					pointRadius: 0,
					borderColor: '#4c4c4c',
					borderWidth: 1,
					data: zeroLine
				}
			]
		};
		userPortfolioChart.update();
	}

	//holds reference to chart so that it can be destroyed when range is changed to add a new chart
	let userPortfolioChart: any;

	let marketTimeSeriesPointsEventListener = () => {};
	let fundingRateIntervalEventListener = () => {};

	onMount(async () => {
		marketTimeSeriesPoints.subscribe(async (state) => {
			if (state != undefined) {
				if (userPortfolioChart == undefined) {
					userPortfolioChart = await createChart();
				} else {
					updateChartData();
				}
			}
		});

		fundingRateInterval.subscribe(async (state) => {
			if (state != undefined && userPortfolioChart != undefined) {
				updateChartData();
			}
		});
	});

	onDestroy(() => {
		marketTimeSeriesPointsEventListener();
		fundingRateIntervalEventListener();
	});

	onMount(() => {
		getCurrentMarketTimeSeriesPoints();
	});

	$: if ($currentMarketIdentifier) {
		getCurrentMarketTimeSeriesPoints();
	}
</script>

<div
	class="w-full h-full p-1 relative z-0 {!showBorder
		? 'sm:min-h-[38vh] sm:h-[38vh] sm:max-h-[38vh]'
		: 'sm:min-h-[45vh] sm:h-[45vh] sm:max-h-[45vh]'}"
	class:border-style={showBorder}
	class:hidden={$chartSelected != 'FundingRate' && $page.url.pathname != '/strategies'}
>
	<div class="w-full h-full">
		<canvas
			class="w-full h-full {!isChartCreated || $loadingMarketTimeSeriesPoints ? 'hidden' : 'block'}"
			id="fundingRateChartElement"
		/>
	</div>
	<div
		class="w-full h-full absolute top-0 z-10 animate-pulse bg-fx-grey-800 opacity-80
		{!isChartCreated || $loadingMarketTimeSeriesPoints ? 'block' : 'hidden'}
		"
	/>
</div>

<style>
	.border-style {
		@apply border-r border-fx-grey-500;
	}
</style>
